import React, { useState, useEffect } from 'react';
import { useBaseUrl } from '../../../utils/utils';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTasks, faCheckCircle, faClock, faDollarSign, faUserCheck,
    faFileInvoice, faCreditCard, faChartLine, faCalendarAlt,
    faPlus, faEdit, faTrash, faEye, faPlay, faPause, faStop,
    faExclamationTriangle, faInfoCircle, faShieldAlt, faSignature,
    faDownload, faUpload, faComments, faHistory, faCog, faSearch
} from '@fortawesome/free-solid-svg-icons';

interface CollaborationTask {
    task_id: string;
    collaboration_id: string;
    title: string;
    description: string;
    task_type: 'consultation' | 'procedure' | 'follow_up' | 'emergency' | 'routine';
    priority: 'low' | 'medium' | 'high' | 'urgent';
    status: 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'on_hold';
    assigned_to: Array<{
        id: number;
        name: string;
        type: string;
        role: string;
    }>;
    patient: {
        id: number;
        name: string;
        contact: string;
    };
    billing_info: {
        billing_mode: 'per_person' | 'per_request' | 'per_milestone';
        amount: number;
        currency: string;
        payment_status: 'pending' | 'partial' | 'completed' | 'refunded';
        invoice_generated: boolean;
        invoice_number?: string;
        payment_date?: string;
    };
    timeline: {
        created_date: string;
        start_date?: string;
        due_date: string;
        completed_date?: string;
        estimated_hours: number;
        actual_hours?: number;
    };
    requirements: string[];
    deliverables: string[];
    notes: string;
    attachments: Array<{
        id: string;
        name: string;
        type: string;
        url: string;
    }>;
    completion_approval: {
        patient_approved: boolean;
        patient_approval_date?: string;
        professional_approved: boolean;
        professional_approval_date?: string;
        admin_approved: boolean;
        admin_approval_date?: string;
    };
    report_generated: boolean;
    report_id?: string;
}

interface Props {
    collaborationId?: string;
    onClose?: () => void;
}

const TaskManagement: React.FC<Props> = ({ collaborationId, onClose }) => {
    const baseUrl = useBaseUrl();
    const [tasks, setTasks] = useState<CollaborationTask[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedTask, setSelectedTask] = useState<CollaborationTask | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [filterPriority, setFilterPriority] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [createForm, setCreateForm] = useState({
        title: '',
        description: '',
        task_type: 'consultation' as const,
        priority: 'medium' as const,
        assigned_to: [] as number[],
        patient_id: '',
        due_date: '',
        estimated_hours: '',
        requirements: '',
        deliverables: '',
        billing_mode: 'per_request' as const,
        amount: ''
    });

    useEffect(() => {
        if (collaborationId) {
            fetchTasks();
        }
    }, [collaborationId]);

    const fetchTasks = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${baseUrl}collaborations/tasks/`, {
                params: { collaboration_id: collaborationId }
            });
            if (response.data.status === 'success') {
                setTasks(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching tasks:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch tasks'
            });
        } finally {
            setLoading(false);
        }
    };

    const createTask = async () => {
        if (!createForm.title || !createForm.description || !createForm.patient_id || !createForm.due_date) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Information',
                text: 'Please fill in all required fields'
            });
            return;
        }

        try {
            const response = await axios.post(`${baseUrl}collaborations/tasks/`, {
                collaboration_id: collaborationId,
                ...createForm,
                requirements: createForm.requirements.split(',').map(r => r.trim()).filter(r => r),
                deliverables: createForm.deliverables.split(',').map(d => d.trim()).filter(d => d),
                amount: parseFloat(createForm.amount),
                estimated_hours: parseFloat(createForm.estimated_hours)
            });

            if (response.data.status === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: 'Task Created',
                    text: 'Task has been successfully created'
                });
                setShowCreateModal(false);
                setCreateForm({
                    title: '',
                    description: '',
                    task_type: 'consultation',
                    priority: 'medium',
                    assigned_to: [],
                    patient_id: '',
                    due_date: '',
                    estimated_hours: '',
                    requirements: '',
                    deliverables: '',
                    billing_mode: 'per_request',
                    amount: ''
                });
                fetchTasks();
            }
        } catch (error) {
            console.error('Error creating task:', error);
            Swal.fire({
                icon: 'error',
                title: 'Creation Failed',
                text: 'Failed to create task. Please try again.'
            });
        }
    };

    const updateTaskStatus = async (taskId: string, newStatus: string) => {
        try {
            const response = await axios.patch(`${baseUrl}collaborations/tasks/${taskId}/`, {
                status: newStatus,
                ...(newStatus === 'completed' && { completed_date: new Date().toISOString() })
            });

            if (response.data.status === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: 'Status Updated',
                    text: `Task status has been updated to ${newStatus}`
                });
                fetchTasks();
            }
        } catch (error) {
            console.error('Error updating task status:', error);
            Swal.fire({
                icon: 'error',
                title: 'Update Failed',
                text: 'Failed to update task status. Please try again.'
            });
        }
    };

    const generateInvoice = async (taskId: string) => {
        try {
            const response = await axios.post(`${baseUrl}collaborations/tasks/${taskId}/generate-invoice/`);
            if (response.data.status === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: 'Invoice Generated',
                    text: 'Invoice has been generated successfully'
                });
                fetchTasks();
            }
        } catch (error) {
            console.error('Error generating invoice:', error);
            Swal.fire({
                icon: 'error',
                title: 'Invoice Generation Failed',
                text: 'Failed to generate invoice. Please try again.'
            });
        }
    };

    const approveTaskCompletion = async (taskId: string, approverType: 'patient' | 'professional' | 'admin') => {
        try {
            const response = await axios.post(`${baseUrl}collaborations/tasks/${taskId}/approve-completion/`, {
                approver_type: approverType
            });

            if (response.data.status === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: 'Approval Granted',
                    text: `${approverType} approval has been recorded`
                });
                fetchTasks();
            }
        } catch (error) {
            console.error('Error approving completion:', error);
            Swal.fire({
                icon: 'error',
                title: 'Approval Failed',
                text: 'Failed to record approval. Please try again.'
            });
        }
    };

    const deleteTask = async (taskId: string) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Delete Task',
            text: 'Are you sure you want to delete this task? This action cannot be undone.',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc3545'
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.delete(`${baseUrl}collaborations/tasks/${taskId}/`);
                if (response.data.status === 'success') {
                    Swal.fire({
                        icon: 'success',
                        title: 'Task Deleted',
                        text: 'Task has been successfully deleted'
                    });
                    fetchTasks();
                }
            } catch (error) {
                console.error('Error deleting task:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Deletion Failed',
                    text: 'Failed to delete task. Please try again.'
                });
            }
        }
    };

    const getStatusBadge = (status: string) => {
        const statusColors = {
            'pending': 'bg-secondary',
            'in_progress': 'bg-primary',
            'completed': 'bg-success',
            'cancelled': 'bg-danger',
            'on_hold': 'bg-warning'
        };
        return statusColors[status as keyof typeof statusColors] || 'bg-secondary';
    };

    const getPriorityBadge = (priority: string) => {
        const priorityColors = {
            'low': 'bg-success',
            'medium': 'bg-warning',
            'high': 'bg-danger',
            'urgent': 'bg-dark'
        };
        return priorityColors[priority as keyof typeof priorityColors] || 'bg-secondary';
    };

    const getBillingModeLabel = (mode: string) => {
        const modeLabels = {
            'per_person': 'Per Person',
            'per_request': 'Per Request',
            'per_milestone': 'Per Milestone'
        };
        return modeLabels[mode as keyof typeof modeLabels] || mode;
    };

    const filteredTasks = tasks.filter(task => {
        const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            task.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
        const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
        return matchesSearch && matchesStatus && matchesPriority;
    });

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="mb-1">
                        <FontAwesomeIcon icon={faTasks} className="me-3 text-primary" />
                        Task Management
                    </h3>
                    <p className="text-muted mb-0">Manage billable tasks and track completion workflows</p>
                </div>
                <div>
                    <button 
                        className="btn btn-primary me-2"
                        onClick={() => setShowCreateModal(true)}
                    >
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        Create Task
                    </button>
                    {onClose && (
                        <button className="btn btn-outline-secondary" onClick={onClose}>
                            Close
                        </button>
                    )}
                </div>
            </div>

            {/* Filters and Search */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <FontAwesomeIcon icon={faSearch} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search tasks..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-2">
                            <select
                                className="form-select"
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                            >
                                <option value="all">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="in_progress">In Progress</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="on_hold">On Hold</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <select
                                className="form-select"
                                value={filterPriority}
                                onChange={(e) => setFilterPriority(e.target.value)}
                            >
                                <option value="all">All Priority</option>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </div>
                        <div className="col-md-5">
                            <div className="d-flex gap-2">
                                <span className="badge bg-secondary">Total: {tasks.length}</span>
                                <span className="badge bg-primary">In Progress: {tasks.filter(t => t.status === 'in_progress').length}</span>
                                <span className="badge bg-success">Completed: {tasks.filter(t => t.status === 'completed').length}</span>
                                <span className="badge bg-warning">Pending: {tasks.filter(t => t.status === 'pending').length}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tasks List */}
            <div className="row">
                {filteredTasks.map((task) => (
                    <div key={task.task_id} className="col-lg-6 mb-4">
                        <div className="card h-100">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-2">
                                    <span className={`badge ${getStatusBadge(task.status)}`}>
                                        {task.status.replace('_', ' ').toUpperCase()}
                                    </span>
                                    <span className={`badge ${getPriorityBadge(task.priority)}`}>
                                        {task.priority.toUpperCase()}
                                    </span>
                                </div>
                                <div className="dropdown">
                                    <button className="btn btn-sm btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown">
                                        <FontAwesomeIcon icon={faCog} />
                                    </button>
                                    <ul className="dropdown-menu">
                                        <li>
                                            <button 
                                                className="dropdown-item"
                                                onClick={() => {
                                                    setSelectedTask(task);
                                                    setShowTaskModal(true);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faEye} className="me-2" />
                                                View Details
                                            </button>
                                        </li>
                                        <li>
                                            <button 
                                                className="dropdown-item"
                                                onClick={() => updateTaskStatus(task.task_id, 'in_progress')}
                                                disabled={task.status !== 'pending'}
                                            >
                                                <FontAwesomeIcon icon={faPlay} className="me-2" />
                                                Start Task
                                            </button>
                                        </li>
                                        <li>
                                            <button 
                                                className="dropdown-item"
                                                onClick={() => updateTaskStatus(task.task_id, 'completed')}
                                                disabled={task.status !== 'in_progress'}
                                            >
                                                <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                                Mark Complete
                                            </button>
                                        </li>
                                        <li>
                                            <button 
                                                className="dropdown-item"
                                                onClick={() => {
                                                    setSelectedTask(task);
                                                    setShowInvoiceModal(true);
                                                }}
                                                disabled={!task.billing_info.invoice_generated}
                                            >
                                                <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
                                                View Invoice
                                            </button>
                                        </li>
                                        <li>
                                            <button 
                                                className="dropdown-item text-danger"
                                                onClick={() => deleteTask(task.task_id)}
                                            >
                                                <FontAwesomeIcon icon={faTrash} className="me-2" />
                                                Delete
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="card-body">
                                <h6 className="card-title">{task.title}</h6>
                                <p className="card-text text-muted small">{task.description}</p>
                                
                                <div className="row mb-3">
                                    <div className="col-6">
                                        <small className="text-muted">Type:</small>
                                        <div>{task.task_type.replace('_', ' ')}</div>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">Due Date:</small>
                                        <div>{new Date(task.timeline.due_date).toLocaleDateString()}</div>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <small className="text-muted">Assigned To:</small>
                                    <div className="d-flex flex-wrap gap-1 mt-1">
                                        {task.assigned_to.map((assignee, index) => (
                                            <span key={index} className="badge bg-light text-dark">
                                                {assignee.name} ({assignee.role})
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <small className="text-muted">Patient:</small>
                                    <div>{task.patient.name}</div>
                                </div>

                                <div className="row">
                                    <div className="col-6">
                                        <small className="text-muted">Billing:</small>
                                        <div className="fw-bold text-success">
                                            {task.billing_info.currency} {task.billing_info.amount}
                                        </div>
                                        <small className="text-muted">
                                            {getBillingModeLabel(task.billing_info.billing_mode)}
                                        </small>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">Payment:</small>
                                        <div>
                                            <span className={`badge ${
                                                task.billing_info.payment_status === 'completed' ? 'bg-success' : 
                                                task.billing_info.payment_status === 'partial' ? 'bg-warning' : 'bg-secondary'
                                            }`}>
                                                {task.billing_info.payment_status.toUpperCase()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="card-footer">
                                <div className="d-flex justify-content-between align-items-center">
                                    <small className="text-muted">
                                        <FontAwesomeIcon icon={faClock} className="me-1" />
                                        Est: {task.timeline.estimated_hours}h
                                        {task.timeline.actual_hours && (
                                            <span className="ms-2">Actual: {task.timeline.actual_hours}h</span>
                                        )}
                                    </small>
                                    <small className="text-muted">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                        {new Date(task.timeline.created_date).toLocaleDateString()}
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {filteredTasks.length === 0 && (
                <div className="text-center py-5">
                    <FontAwesomeIcon icon={faTasks} size="3x" className="text-muted mb-3" />
                    <h5>No Tasks Found</h5>
                    <p className="text-muted">Create your first task to get started</p>
                </div>
            )}

            {/* Create Task Modal */}
            {showCreateModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                    Create New Task
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close"
                                    onClick={() => setShowCreateModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-8">
                                        <div className="mb-3">
                                            <label className="form-label">Task Title *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={createForm.title}
                                                onChange={(e) => setCreateForm({...createForm, title: e.target.value})}
                                                placeholder="Enter task title"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <label className="form-label">Task Type</label>
                                            <select
                                                className="form-select"
                                                value={createForm.task_type}
                                                onChange={(e) => setCreateForm({...createForm, task_type: e.target.value as any})}
                                            >
                                                <option value="consultation">Consultation</option>
                                                <option value="procedure">Procedure</option>
                                                <option value="follow_up">Follow Up</option>
                                                <option value="emergency">Emergency</option>
                                                <option value="routine">Routine</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="mb-3">
                                    <label className="form-label">Description *</label>
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        value={createForm.description}
                                        onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                                        placeholder="Enter task description"
                                        required
                                    />
                                </div>

                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <label className="form-label">Priority</label>
                                            <select
                                                className="form-select"
                                                value={createForm.priority}
                                                onChange={(e) => setCreateForm({...createForm, priority: e.target.value as any})}
                                            >
                                                <option value="low">Low</option>
                                                <option value="medium">Medium</option>
                                                <option value="high">High</option>
                                                <option value="urgent">Urgent</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <label className="form-label">Due Date *</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={createForm.due_date}
                                                onChange={(e) => setCreateForm({...createForm, due_date: e.target.value})}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <label className="form-label">Estimated Hours</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={createForm.estimated_hours}
                                                onChange={(e) => setCreateForm({...createForm, estimated_hours: e.target.value})}
                                                placeholder="0.0"
                                                step="0.5"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Requirements</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={createForm.requirements}
                                                onChange={(e) => setCreateForm({...createForm, requirements: e.target.value})}
                                                placeholder="Enter requirements separated by commas"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Deliverables</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={createForm.deliverables}
                                                onChange={(e) => setCreateForm({...createForm, deliverables: e.target.value})}
                                                placeholder="Enter deliverables separated by commas"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Billing Mode</label>
                                            <select
                                                className="form-select"
                                                value={createForm.billing_mode}
                                                onChange={(e) => setCreateForm({...createForm, billing_mode: e.target.value as any})}
                                            >
                                                <option value="per_person">Per Person</option>
                                                <option value="per_request">Per Request</option>
                                                <option value="per_milestone">Per Milestone</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Amount</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={createForm.amount}
                                                onChange={(e) => setCreateForm({...createForm, amount: e.target.value})}
                                                placeholder="0.00"
                                                step="0.01"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={() => setShowCreateModal(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={createTask}
                                >
                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                    Create Task
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Task Details Modal */}
            {showTaskModal && selectedTask && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <FontAwesomeIcon icon={faTasks} className="me-2" />
                                    Task Details: {selectedTask.title}
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close"
                                    onClick={() => setShowTaskModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-8">
                                        <div className="card mb-3">
                                            <div className="card-header">
                                                <h6 className="mb-0">Task Information</h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <p><strong>Type:</strong> {selectedTask.task_type.replace('_', ' ')}</p>
                                                        <p><strong>Priority:</strong> 
                                                            <span className={`badge ${getPriorityBadge(selectedTask.priority)} ms-2`}>
                                                                {selectedTask.priority.toUpperCase()}
                                                            </span>
                                                        </p>
                                                        <p><strong>Status:</strong> 
                                                            <span className={`badge ${getStatusBadge(selectedTask.status)} ms-2`}>
                                                                {selectedTask.status.replace('_', ' ').toUpperCase()}
                                                            </span>
                                                        </p>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <p><strong>Created:</strong> {new Date(selectedTask.timeline.created_date).toLocaleDateString()}</p>
                                                        <p><strong>Due Date:</strong> {new Date(selectedTask.timeline.due_date).toLocaleDateString()}</p>
                                                        <p><strong>Estimated Hours:</strong> {selectedTask.timeline.estimated_hours}h</p>
                                                    </div>
                                                </div>
                                                
                                                <div className="mb-3">
                                                    <strong>Description:</strong>
                                                    <p className="mt-2">{selectedTask.description}</p>
                                                </div>

                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <strong>Requirements:</strong>
                                                        <ul className="mt-2">
                                                            {selectedTask.requirements.map((req, index) => (
                                                                <li key={index}>{req}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <strong>Deliverables:</strong>
                                                        <ul className="mt-2">
                                                            {selectedTask.deliverables.map((del, index) => (
                                                                <li key={index}>{del}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="card">
                                            <div className="card-header">
                                                <h6 className="mb-0">Completion Approval</h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-4 text-center">
                                                        <div className={`p-3 border rounded ${selectedTask.completion_approval.patient_approved ? 'bg-success text-white' : 'bg-light'}`}>
                                                            <FontAwesomeIcon 
                                                                icon={selectedTask.completion_approval.patient_approved ? faCheckCircle : faClock} 
                                                                size="2x" 
                                                                className="mb-2"
                                                            />
                                                            <div>Patient Approval</div>
                                                            {selectedTask.completion_approval.patient_approved && (
                                                                <small>{new Date(selectedTask.completion_approval.patient_approval_date!).toLocaleDateString()}</small>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4 text-center">
                                                        <div className={`p-3 border rounded ${selectedTask.completion_approval.professional_approved ? 'bg-success text-white' : 'bg-light'}`}>
                                                            <FontAwesomeIcon 
                                                                icon={selectedTask.completion_approval.professional_approved ? faCheckCircle : faClock} 
                                                                size="2x" 
                                                                className="mb-2"
                                                            />
                                                            <div>Professional Approval</div>
                                                            {selectedTask.completion_approval.professional_approved && (
                                                                <small>{new Date(selectedTask.completion_approval.professional_approval_date!).toLocaleDateString()}</small>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4 text-center">
                                                        <div className={`p-3 border rounded ${selectedTask.completion_approval.admin_approved ? 'bg-success text-white' : 'bg-light'}`}>
                                                            <FontAwesomeIcon 
                                                                icon={selectedTask.completion_approval.admin_approved ? faCheckCircle : faClock} 
                                                                size="2x" 
                                                                className="mb-2"
                                                            />
                                                            <div>Admin Approval</div>
                                                            {selectedTask.completion_approval.admin_approved && (
                                                                <small>{new Date(selectedTask.completion_approval.admin_approval_date!).toLocaleDateString()}</small>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="col-md-4">
                                        <div className="card mb-3">
                                            <div className="card-header">
                                                <h6 className="mb-0">Billing Information</h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="mb-3">
                                                    <strong>Amount:</strong>
                                                    <div className="h4 text-success">
                                                        {selectedTask.billing_info.currency} {selectedTask.billing_info.amount}
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <strong>Billing Mode:</strong>
                                                    <div>{getBillingModeLabel(selectedTask.billing_info.billing_mode)}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <strong>Payment Status:</strong>
                                                    <div>
                                                        <span className={`badge ${
                                                            selectedTask.billing_info.payment_status === 'completed' ? 'bg-success' : 
                                                            selectedTask.billing_info.payment_status === 'partial' ? 'bg-warning' : 'bg-secondary'
                                                        }`}>
                                                            {selectedTask.billing_info.payment_status.toUpperCase()}
                                                        </span>
                                                    </div>
                                                </div>
                                                {selectedTask.billing_info.invoice_generated && (
                                                    <div className="mb-3">
                                                        <strong>Invoice:</strong>
                                                        <div>{selectedTask.billing_info.invoice_number}</div>
                                                    </div>
                                                )}
                                                {!selectedTask.billing_info.invoice_generated && selectedTask.status === 'completed' && (
                                                    <button 
                                                        className="btn btn-primary w-100"
                                                        onClick={() => generateInvoice(selectedTask.task_id)}
                                                    >
                                                        <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
                                                        Generate Invoice
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="card">
                                            <div className="card-header">
                                                <h6 className="mb-0">Actions</h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="d-grid gap-2">
                                                    {selectedTask.status === 'pending' && (
                                                        <button 
                                                            className="btn btn-primary"
                                                            onClick={() => updateTaskStatus(selectedTask.task_id, 'in_progress')}
                                                        >
                                                            <FontAwesomeIcon icon={faPlay} className="me-2" />
                                                            Start Task
                                                        </button>
                                                    )}
                                                    {selectedTask.status === 'in_progress' && (
                                                        <button 
                                                            className="btn btn-success"
                                                            onClick={() => updateTaskStatus(selectedTask.task_id, 'completed')}
                                                        >
                                                            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                                            Mark Complete
                                                        </button>
                                                    )}
                                                    {selectedTask.status === 'completed' && !selectedTask.completion_approval.patient_approved && (
                                                        <button 
                                                            className="btn btn-info"
                                                            onClick={() => approveTaskCompletion(selectedTask.task_id, 'patient')}
                                                        >
                                                            <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                                                            Approve as Patient
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={() => setShowTaskModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskManagement;
